import { Capacitor, registerPlugin } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { Geolocation } from '@capacitor/geolocation';
import { BASE_URL } from '../utils/constant';
import { localStore } from '../App';

/**
 * Native critical-alert plugin:
 * - iOS: CriticalAlertsPlugin.swift (true Critical Alerts entitlement + foreground
 *   presentation options including .sound)
 * - Android: CriticalAlertsPlugin.java listens to background-geolocation LocalBroadcasts,
 *   runs native geofence checks, and plays USAGE_ALARM via DangerAlarmController
 *   (WebView/JS is often paused while locked, so native must drive the alarm)
 */
export interface CriticalAlertsPlugin {
  requestCriticalPermissions(): Promise<{ granted: boolean; criticalAlert: string }>;
  checkCriticalPermissions(): Promise<{ criticalAlert: string }>;
  notify(options: {
    title: string;
    body: string;
    sound?: string;
    volume?: number;
    extra?: Record<string, unknown>;
  }): Promise<void>;
  /** Android: stop the looping USAGE_ALARM siren. No-op on iOS. */
  stop(): Promise<void>;
  /**
   * Android only: push zone polygons into the native monitor so geofence checks
   * keep working while the WebView is paused. Not implemented on iOS.
   */
  syncZones(options: {
    zones: Array<{
      danger_zone_id: number;
      title: string;
      body?: string;
      danger_start_date: string;
      danger_end_date: string;
      danger_polygon_geojson: string;
    }>;
  }): Promise<void>;
}

export const CriticalAlerts = registerPlugin<CriticalAlertsPlugin>('CriticalAlerts');

const STORAGE_KEY = 'dangerZonesList';
/** How often to test the current GPS position against cached polygons. */
const GEOFENCE_INTERVAL_MS = 3_000; // 3 seconds
/** How often to refresh the danger-zone list from the server. */
const FETCH_INTERVAL_MS = 300_000; // 5 minutes seconds
/** Re-fire the alert while the user remains inside a zone (also after "Ho capito"). */
const REALERT_MS = 30_000;

/** Dispatched on the window when the user enters a danger zone (for in-app UI). */
export const DANGER_ZONE_ALERT_EVENT = 'adpo:danger-zone-alert';

/** Dispatched when the fetched danger-zone list changes (add/remove/geometry/dates). */
export const DANGER_ZONES_UPDATED_EVENT = 'adpo:danger-zones-updated';

export interface DangerZone {
  id: number;
  title: string;
  body?: string;
  danger_polygon_geojson: string | Record<string, unknown>; // GeoJSON Geometry (Polygon)
  danger_end_date: string;        // 'YYYY-MM-DD'
  danger_start_date: string;      // 'YYYY-MM-DD'
  danger_zone_id: number;
}

/**
 * Ray-casting point-in-polygon for a GeoJSON Polygon ring.
 * Coordinates are [lng, lat] pairs.
 */
function isPointInPolygon(lat: number, lng: number, polygon: number[][]): boolean {
  let inside = false;
  const n = polygon.length;
  let j = n - 1;
  for (let i = 0; i < n; i++) {
    const xi = polygon[i][0]; // lng
    const yi = polygon[i][1]; // lat
    const xj = polygon[j][0];
    const yj = polygon[j][1];
    const intersect =
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
    j = i;
  }
  return inside;
}

/** Parse API date field (YYYY-MM-DD) as local calendar date, not UTC midnight. */
function parseLocalDateOnly(value: string): Date | null {
  if (!value || typeof value !== 'string') {
    return null;
  }
  const datePart = value.trim().slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(datePart);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  return new Date(year, month - 1, day);
}

type DangerPolygonGeometry = {
  type: 'Polygon';
  coordinates: number[][][];
};

function parseDangerZoneGeometry(raw: string | Record<string, unknown>): DangerPolygonGeometry | null {
  try {
    let parsed: unknown =
      typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    const obj = parsed as Record<string, unknown>;
    if (obj.type === 'Feature' && obj.geometry && typeof obj.geometry === 'object') {
      parsed = obj.geometry;
    }
    const geom = parsed as DangerPolygonGeometry;
    if (geom.type !== 'Polygon' || !Array.isArray(geom.coordinates)) {
      return null;
    }
    return geom;
  }
  catch {
    return null;
  }
}

/**
 * Returns true if the given lat/lng is inside any ring of the GeoJSON polygon.
 */
function isInsideGeoJsonPolygon(
  lat: number,
  lng: number,
  geojsonRaw: string | Record<string, unknown>
): boolean {
  const geom = parseDangerZoneGeometry(geojsonRaw);
  if (!geom) {
    return false;
  }
  const outerRing: number[][] = geom.coordinates[0];
  if (!outerRing || outerRing.length < 3) {
    return false;
  }
  return isPointInPolygon(lat, lng, outerRing);
}

/**
 * Returns true if the danger zone is still valid (today is within start/end dates).
 */
function isDangerZoneActive(zone: DangerZone): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = parseLocalDateOnly(zone.danger_start_date);
  const end = parseLocalDateOnly(zone.danger_end_date);
  if (!start || !end) {
    return false;
  }
  return today >= start && today <= end;
}

class DangerZoneMonitorService {
  private geofenceIntervalId: ReturnType<typeof setInterval> | null = null;
  private fetchIntervalId: ReturnType<typeof setInterval> | null = null;
  /** Last time we fired an alert per zone — re-alert every REALERT_MS while still inside. */
  private lastAlertAtByZoneId: Map<number, number> = new Map();
  private currentLat: number | null = null;
  private currentLng: number | null = null;
  /** Last zones from the API (or cache); used by the 30s geofence loop. */
  private cachedZones: DangerZone[] = [];
  /** Fingerprint of last known zone list; used to detect changes for map refresh. */
  private lastZonesFingerprint: string | null = null;

  /** Call this from App.tsx whenever the GPS position changes */
  updatePosition(lat: number, lng: number) {
    this.currentLat = lat;
    this.currentLng = lng;

    // Run a geofence check on EVERY fix, not just the first one. When the screen
    // is locked / the app is backgrounded, the OS suspends the WebView's JS
    // timers, so the 30s setInterval loop stops firing. The native
    // BackgroundGeolocation callback is the only thing that keeps running in the
    // background, so detection must be driven from here to work while locked.
    if (this.cachedZones.length === 0)
    {
      // Zones not loaded yet: checkNow() fetches the list (falling back to the
      // local-storage cache) and then runs the check.
      void this.checkNow();
    }
    else
    {
      // Zones already cached: run the cheap in-memory check directly, avoiding a
      // network fetch on every position update.
      void this.runGeofenceCheck();
    }
  }

  /** Run a geofence check immediately (e.g. after a fresh fix on the map screen). */
  async checkNow() {
    if (this.cachedZones.length === 0)
    {
      await this.refreshZonesFromApi();
    }
    await this.runGeofenceCheck();
  }

  /** Start API refresh (60s) + geofence (30s) loops. Call once on app init. */
  async start() {
    if (this.geofenceIntervalId !== null || this.fetchIntervalId !== null)
    {
      return; // already running
    }

    // Do not request notification permission here — App.tsx asks once (FirebaseMessaging)
    // before location, so Android does not get parallel permission dialogs.

    await this.refreshZonesFromApi();
    await this.runGeofenceCheck();

    this.fetchIntervalId = setInterval(() => {
      void this.refreshZonesFromApi();
    }, FETCH_INTERVAL_MS);

    this.geofenceIntervalId = setInterval(() => {
      void this.runGeofenceCheck();
    }, GEOFENCE_INTERVAL_MS);

    // Actively grab one immediate high-accuracy fix instead of waiting for the
    // BackgroundGeolocation watcher, whose first fix can be slow/unreliable on a
    // cold start (especially on iOS with a stationary user). This makes the
    // "already inside a danger zone at launch" alert fire early and consistently,
    // without needing the user to open the map screen first.
    void this.primeCurrentPosition();
  }

  /**
   * One-shot high-accuracy fix used only to seed the monitor at startup.
   * Feeds the position through updatePosition(), which triggers the first-fix check.
   */
  private async primeCurrentPosition() {
    if (Capacitor.getPlatform() === 'web')
    {
      return;
    }
    if (this.currentLat !== null && this.currentLng !== null)
    {
      return; // a fix already arrived from a watcher
    }
    try
    {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10_000,
      });
      this.updatePosition(position.coords.latitude, position.coords.longitude);
    }
    catch (e)
    {
      console.warn('DangerZoneMonitorService :: primeCurrentPosition failed', e);
    }
  }

  stop() {
    if (this.geofenceIntervalId !== null)
    {
      clearInterval(this.geofenceIntervalId);
      this.geofenceIntervalId = null;
    }
    if (this.fetchIntervalId !== null)
    {
      clearInterval(this.fetchIntervalId);
      this.fetchIntervalId = null;
    }
  }

  /**
   * Call when the user dismisses the in-app / native alert ("Ho capito").
   * Starts a fresh 30s silence window before the next re-alert while still inside.
   */
  noteAlertDismissed() {
    const now = Date.now();
    this.lastAlertAtByZoneId.forEach((_lastAlertAt, id) => {
      this.lastAlertAtByZoneId.set(id, now);
    });
  }

  /** Fetch zones from the server (or storage fallback) and notify the map if the list changed. */
  private async refreshZonesFromApi() {
    console.log('DangerZoneMonitorService :: ## refreshing zones from api ##');
    const zones = await this.fetchActiveZones();
    this.cachedZones = zones;
    await this.syncZonesToAndroidNative(zones);
    this.notifyIfZonesChanged(zones);
  }

  /**
   * Android only: keep the native geofence monitor in sync. iOS is untouched —
   * Critical Alerts there are delivered from JS via the Swift plugin.
   */
  private async syncZonesToAndroidNative(zones: DangerZone[]) {
    if (Capacitor.getPlatform() !== 'android')
    {
      return;
    }
    try
    {
      await CriticalAlerts.syncZones({
        zones: zones.map((z) => ({
          danger_zone_id: z.danger_zone_id,
          title: z.title,
          body: z.body ?? '',
          danger_start_date: z.danger_start_date,
          danger_end_date: z.danger_end_date,
          danger_polygon_geojson:
            typeof z.danger_polygon_geojson === 'string'
              ? z.danger_polygon_geojson
              : JSON.stringify(z.danger_polygon_geojson),
        })),
      });
    }
    catch (e)
    {
      console.warn('DangerZoneMonitorService :: Android syncZones failed', e);
    }
  }

  /** Test the current GPS position against the cached danger-zone polygons. */
  private async runGeofenceCheck() 
  {
    console.log('DangerZoneMonitorService :: ** running geofence check **');

    if (this.currentLat === null || this.currentLng === null)
    {
      console.log('DangerZoneMonitorService :: position not yet available, skipping geofence check');
      return;
    }

    const zones = this.cachedZones;

    if (!zones || zones.length === 0)
    {
      // No active zones – clear so zones that come back will re-alert
      this.lastAlertAtByZoneId.clear();
      return;
    }

    const lat = this.currentLat;
    const lng = this.currentLng;
    const now = Date.now();

    let userIsInAtLeastOneZone = false;

    for (const zone of zones)
    {
      if (!zone.danger_polygon_geojson || !isDangerZoneActive(zone))
      {
        continue;
      }

      const inside = isInsideGeoJsonPolygon(lat, lng, zone.danger_polygon_geojson);

      if (inside)
      {
        userIsInAtLeastOneZone = true;
        const lastAlertAt = this.lastAlertAtByZoneId.get(zone.danger_zone_id) ?? 0;
        if (now - lastAlertAt >= REALERT_MS)
        {
          console.log(
            `DangerZoneMonitorService :: alerting zone "${zone.title}" (id ${zone.danger_zone_id})`
            + (lastAlertAt === 0 ? ' [first]' : ' [re-alert 30s]')
          );
          this.lastAlertAtByZoneId.set(zone.danger_zone_id, now);
          await this.fireCriticalAlert(zone);
        }
        else
        {
          console.log(
            `DangerZoneMonitorService :: still inside zone "${zone.title}" `
            + `(id ${zone.danger_zone_id}), next re-alert in `
            + `${Math.ceil((REALERT_MS - (now - lastAlertAt)) / 1000)}s`
          );
        }
      }
      else
      {
        // User left this zone – clear so they get alerted immediately on re-enter
        this.lastAlertAtByZoneId.delete(zone.danger_zone_id);
      }
    }

    if (!userIsInAtLeastOneZone)
    {
      console.log(
        `DangerZoneMonitorService :: user is not in any active danger zone (lat=${lat}, lng=${lng})`
      );
    }
  }

  /** Compare zone list to the previous fetch; notify listeners when it changed. */
  private notifyIfZonesChanged(zones: DangerZone[]) {
    const fingerprint = zones
      .map((z) => {
        const geom =
          typeof z.danger_polygon_geojson === 'string'
            ? z.danger_polygon_geojson
            : JSON.stringify(z.danger_polygon_geojson);
        return `${z.danger_zone_id}|${z.danger_start_date}|${z.danger_end_date}|${geom}`;
      })
      .sort()
      .join(';;');

    if (this.lastZonesFingerprint === null)
    {
      // First successful fetch: store baseline, do not force a map refresh.
      this.lastZonesFingerprint = fingerprint;
      return;
    }

    if (this.lastZonesFingerprint === fingerprint)
    {
      return;
    }

    this.lastZonesFingerprint = fingerprint;
    console.log(
      `DangerZoneMonitorService :: danger zone list changed (${zones.length} zone(s)), notifying map`
    );

    if (typeof window === 'undefined')
    {
      return;
    }

    window.dispatchEvent(
      new CustomEvent(DANGER_ZONES_UPDATED_EVENT, {
        detail: { zones },
      }),
    );
  }

  private emitDangerZoneAlert(zone: DangerZone) {
    if (typeof window === 'undefined')
    {
      return;
    }
    window.dispatchEvent(
      new CustomEvent(DANGER_ZONE_ALERT_EVENT, {
        detail: {
          title: zone.title,
          body: zone.body,
          zoneId: zone.danger_zone_id,
        },
      }),
    );
  }

  /**
   * Fire-and-forget metrics: tell the server a critical alert was delivered.
   * No retry on failure (offline / network errors are ignored).
   */
  private reportCriticalAlertFired(zone: DangerZone) {
    const platform = Capacitor.getPlatform();
    if (platform === 'web')
    {
      return;
    }

    const lat = this.currentLat;
    const lng = this.currentLng;
    if (lat === null || lng === null)
    {
      return;
    }

    void (async () => {
      let deviceId = 'unknown';
      try
      {
        const key = 'po_app_fallback_uuid';
        const fromStorage = window.localStorage.getItem(key);
        if (fromStorage && fromStorage.trim().length > 0)
        {
          deviceId = fromStorage;
        }

        const devId = await Device.getId();
        if (devId && typeof devId.identifier === 'string' && devId.identifier.trim().length > 0)
        {
          deviceId = devId.identifier;
        }
      }
      catch
      {
        /* keep fallback / unknown */
      }

      const postData = {
        act: 23,
        h: 'gkzLoFk0MN58TGqCJj',
        dangerZoneId: zone.danger_zone_id,
        zoneTitle: zone.title,
        lat,
        lng,
        uuid: deviceId,
        platform,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      };

      try
      {
        await fetch(`${BASE_URL}/poServer.php`, {
          method: 'POST',
          mode: 'cors',
          body: JSON.stringify(postData),
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        });
      }
      catch
      {
        // Offline or network error: do not retry.
      }
    })();
  }

  private async fireCriticalAlert(zone: DangerZone) {
    // Note: re-alert cadence is enforced by lastAlertAtByZoneId in runGeofenceCheck
    // (and by DangerZoneNativeMonitor's 30s Handler on Android while locked).

    if (Capacitor.getPlatform() === 'web') {
      console.warn(`DangerZoneMonitorService :: CRITICAL ALERT – ${zone.title}`);
      this.emitDangerZoneAlert(zone);
      return;
    }

    this.reportCriticalAlertFired(zone);
    this.emitDangerZoneAlert(zone);

    let appIsActive = true;
    try
    {
      const state = await App.getState();
      appIsActive = state.isActive;
    }
    catch
    {
      /* use default: still try local notification */
    }

    // iOS: always deliver a Critical Alert (foreground + background). In foreground
    // the in-app dialog still shows; the native notify supplies the critical sound
    // (Web Audio often stays silent in WKWebView). Presentation options must
    // include .sound — see CriticalAlertsPlugin.
    if (Capacitor.getPlatform() === 'ios')
    {
      try
      {
        await CriticalAlerts.notify({
          title: '⚠️ ZONA DI PERICOLO',
          body: zone.title,
          sound: 'danger_alarm.wav',
          volume: 1.0,
          extra: {
            alert_type: 'danger_zone',
            zone_id: zone.danger_zone_id,
          },
        });
        console.log(
          `DangerZoneMonitorService :: critical alert delivered for zone "${zone.title}"`
          + ` (app ${appIsActive ? 'foreground' : 'background'})`,
        );
      }
      catch (e)
      {
        console.error('DangerZoneMonitorService :: failed to deliver critical alert', e);
      }
      return;
    }

    // Android: in-app dialog + Web Audio/Haptics while foreground. Native monitor
    // (LocalBroadcast → DangerZoneNativeMonitor) drives the alarm when background /
    // locked; notify() here is a backup when JS is awake and the app is not active.
    if (appIsActive)
    {
      console.log(`DangerZoneMonitorService :: in-app alert for zone "${zone.title}" (app foreground)`);
      return;
    }

    try
    {
      await CriticalAlerts.notify({
        title: '⚠️ ZONA DI PERICOLO',
        body: zone.title,
        sound: 'danger_alarm.wav',
        volume: 1.0,
        extra: {
          alert_type: 'danger_zone',
          zone_id: zone.danger_zone_id,
        },
      });
      console.log(`DangerZoneMonitorService :: Android backup notify for zone "${zone.title}"`);
    }
    catch (e)
    {
      console.error('DangerZoneMonitorService :: Android backup notify failed', e);
    }
  }

  /** Fetch active danger zones: always tries server first, falls back to local storage. */
  private async fetchActiveZones(): Promise<DangerZone[]> {
    try {
      const url = `${BASE_URL}/poServer.php?act=16&h=gkzLoFk0MN58TGqCJj`;
      const res = await fetch(url);
      const json = await res.json();
      if (json && Array.isArray(json.content)) {
        const zones: DangerZone[] = json.content.filter(
          (n: any) => n.danger_zone_id && n.danger_polygon_geojson
        );
        await localStore.set(STORAGE_KEY, zones);
        return zones;
      }
    } catch (e) {
      console.warn('DangerZoneMonitorService :: server fetch failed, using cache', e);
    }

    // Fallback: local storage cache
    try {
      const cached = await localStore.get(STORAGE_KEY);
      if (Array.isArray(cached)) return cached as DangerZone[];
    } catch {
      // ignore
    }
    return [];
  }
}

// Singleton
const dangerZoneMonitor = new DangerZoneMonitorService();
export default dangerZoneMonitor;
